import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { api, unwrap } from '@/api/client'
import { ApiError } from '@/api/errors'
import type { AuthUser } from '@/api/types'
import { getInitData } from '@/lib/telegram'
import { getToken, setToken } from './session'

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'

export interface AuthContextValue {
  status: AuthStatus
  user: AuthUser | null
  error: string | null
  loginWithTelegram: () => Promise<void>
  loginWithBootstrap: (secret: string) => Promise<void>
  logout: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

async function fetchMe(): Promise<AuthUser> {
  return unwrap(api.GET('/api/v1/me'))
}

async function telegramLogin(initData: string): Promise<AuthUser> {
  const res = await unwrap(api.POST('/api/v1/auth/telegram', { body: { init_data: initData } }))
  setToken(res.access_token)
  return res.user
}

interface InternalState {
  status: AuthStatus
  user: AuthUser | null
  error: string | null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<InternalState>({
    status: 'loading',
    user: null,
    error: null,
  })
  const bootstrapped = useRef(false)

  const loginWithTelegram = useCallback(async () => {
    const initData = getInitData()
    if (!initData) {
      setState({
        status: 'unauthenticated',
        user: null,
        error: 'Откройте приложение внутри Telegram, чтобы войти.',
      })
      return
    }
    setState((s) => ({ ...s, status: 'loading', error: null }))
    try {
      const user = await telegramLogin(initData)
      setState({ status: 'authenticated', user, error: null })
    } catch (e) {
      setToken(null)
      setState({
        status: 'unauthenticated',
        user: null,
        error: e instanceof ApiError ? e.message : 'Не удалось войти.',
      })
    }
  }, [])

  const loginWithBootstrap = useCallback(async (secret: string) => {
    setState((s) => ({ ...s, status: 'loading', error: null }))
    try {
      const res = await unwrap(api.POST('/api/v1/auth/bootstrap', { body: { secret } }))
      setToken(res.access_token)
      setState({ status: 'authenticated', user: res.user, error: null })
    } catch (e) {
      setToken(null)
      setState({
        status: 'unauthenticated',
        user: null,
        error: e instanceof ApiError ? e.message : 'Не удалось войти.',
      })
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await unwrap(api.POST('/api/v1/auth/logout'))
    } catch {
      /* best-effort: token is cleared regardless */
    }
    setToken(null)
    setState({ status: 'unauthenticated', user: null, error: null })
  }, [])

  useEffect(() => {
    if (bootstrapped.current) return
    bootstrapped.current = true
    void (async () => {
      if (getToken()) {
        try {
          const user = await fetchMe()
          setState({ status: 'authenticated', user, error: null })
          return
        } catch (e) {
          if (!(e instanceof ApiError && e.isAuth)) {
            setState({
              status: 'unauthenticated',
              user: null,
              error: e instanceof ApiError ? e.message : 'Ошибка соединения.',
            })
            return
          }
          setToken(null) // stale session — fall through to Telegram login
        }
      }
      await loginWithTelegram()
    })()
  }, [loginWithTelegram])

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, loginWithTelegram, loginWithBootstrap, logout }),
    [state, loginWithTelegram, loginWithBootstrap, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
